import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import DataTable from "../../../components/RestaurantAdminComponents/DataTable";
import FilterToolbar from "../../../components/Common/FilterToolbar";
import { Container } from "reactstrap";
import { get, deleteRequest } from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import DeleteConfirmationModal from "../../../components/Common/DeleteConfirmationModal";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import { extractErrorMessage } from "../../../utils/errorHandler";
import trashIcon from "../../../assets/images/support/trash-icon.svg";

/* -------------------------------------------------------------------------
 * AdminCustomerFinancialAccountsPage — Customer Financial Accounts management
 * ---------------------------------------------------------------------- */

const formatDate = (dateString) => {
  if (!dateString || dateString === 'Unknown') return 'Unknown';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const columns = (onDelete) => [
  { key: "id", label: "ID", width: "0.8fr" },
  { key: "customer_name", label: "Customer Name", width: "1.5fr" },
  { key: "customer_email", label: "Customer Email", width: "1.5fr" },
  { key: "institution_name", label: "Institution", width: "1.2fr" },
  { key: "display_name", label: "Account Name", width: "1.2fr" },
  { key: "last4", label: "Last 4", width: "0.6fr" },
  { key: "status", label: "Status", width: "0.8fr" },
  { key: "linked_at", label: "Linked At", width: "1.2fr", render: (row) => formatDate(row.linked_at) },
  {
    key: "actions",
    label: "",
    width: "80px",
    align: "center",
    render: (row) => (
      <button
        type="button"
        onClick={() => onDelete && onDelete(row.id)}
        className="delete-btn"
        aria-label={`Delete account ${row.id}`}
        style={{
          padding: "8px",
          backgroundColor: "transparent",
          border: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img src={trashIcon} alt="Delete" style={{ width: "20px", height: "20px" }} />
      </button>
    ),
  },
];

const AdminCustomerFinancialAccountsPage = ({ onFilterClick, onExportClick }) => {
  document.title = "Customer Financial Accounts | Omaha Food Lovers";
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [customers, setCustomers] = useState([]);

  // Delete modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);

  // Configure toastr options
  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  const buildQueryParams = (pageNum = 1) => {
    const params = new URLSearchParams();
    params.append("page", pageNum);
    params.append("page_size", 20);

    if (search.trim()) {
      params.append("search", search.trim());
    }

    if (customerId) {
      params.append("user_id", customerId);
    }

    return params.toString();
  };

  const fetchAccounts = async (pageNum = 1, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await get(`${endpoints.adminCustomersFinancialAccounts}?${buildQueryParams(pageNum)}`);

      if (response.data?.success) {
        const data = response.data.data;
        const rawAccounts = data.data || [];

        // Transform API data to component format
        const newAccounts = rawAccounts.map(account => ({
          id: account.id,
          customer_name: account.user?.full_name || `${account.user?.first_name || ''} ${account.user?.last_name || ''}`.trim() || 'Unknown',
          customer_email: account.user?.email || 'N/A',
          institution_name: account.institution_name || 'N/A',
          display_name: account.display_name || 'N/A',
          last4: account.last4 || 'N/A',
          status: account.status || 'Unknown',
          linked_at: account.linked_at_formatted || account.linked_at || 'Unknown',
        }));

        if (isLoadMore) {
          setAccounts((prev) => [...prev, ...newAccounts]);
        } else {
          setAccounts(newAccounts);
        }

        setHasMore(newAccounts.length === 20);
        setPage(pageNum);
      }
    } catch (error) {
      console.error("Failed to fetch customer financial accounts:", error);
      const errorMessage = extractErrorMessage(error);
      toastr.error(errorMessage);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchAccounts(page + 1, true);
    }
  };

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
    setHasMore(true);
  };

  const fetchCustomers = async () => {
    try {
      const response = await get(`${endpoints.adminAllUsers}?page=1&per_page=100&sort_by=created_at&sort_direction=desc&member_type=customer`);
      if (response.data?.data) {
        const data = response.data.data;
        const rawCustomers = data.data || [];

        const customerOptions = rawCustomers.map((c) => ({
          value: c.id,
          label: `${c.full_name || c.name || 'Unknown'} (${c.email || 'No email'})`,
        }));

        setCustomers(customerOptions);
      }
    } catch (error) {
      console.error("Failed to fetch customers:", error);
    }
  };

  const handleDeleteClick = (accountId) => {
    setAccountToDelete(accountId);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!accountToDelete) return;

    try {
      const deleteEndpoint = endpoints.adminDeleteCustomerFinancialAccount.replace('{{financial_account_id}}', accountToDelete);
      await deleteRequest(deleteEndpoint);
      
      toastr.success("Financial account deleted successfully!");
      setIsDeleteModalOpen(false);
      setAccountToDelete(null);
      
      // Refresh the list
      fetchAccounts(1, false);
    } catch (error) {
      console.error("Failed to delete financial account:", error);
      const errorMessage = extractErrorMessage(error);
      toastr.error(errorMessage);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setAccountToDelete(null);
  };

  const handleExport = async () => {
    try {
      const response = await get(`${endpoints.adminExport}?type=customer_financial_accounts`);
      
      // Create a blob from the response data
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'customer_financial_accounts.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toastr.success("Export successful!");
    } catch (error) {
      console.error("Failed to export data:", error);
      const errorMessage = extractErrorMessage(error);
      toastr.error(errorMessage);
    }
  };

  // Filter configuration for FilterToolbar
  const filters = [
    {
      key: "customer",
      type: "select",
      label: "Customer",
      value: customerId,
      onChange: setCustomerId,
      options: customers,
      searchable: true,
    },
  ];

  useEffect(() => {
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchAccounts(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, customerId]);

  return (
    <div className="page-content rp-page-content">
      <Container fluid>
        <div className="rp-page">
          <div>
            <h2 className="rp-card__title" style={{ margin: 0 }}>Customer Financial Accounts</h2>
            <p className="rp-card__subtitle" style={{ margin: 0 }}>Overview of customer payment methods and financial accounts.</p>
          </div>
          <FilterToolbar
            searchValue={search}
            onSearchChange={handleSearchChange}
            searchPlaceholder="Search by customer name or email"
            filters={filters}
            showExport
            onExport={handleExport}
          />

        <DataTable
          columns={columns(handleDeleteClick)}
          rows={accounts}
          getRowKey={(r) => r.id}
          loading={loading}
          loadingMore={loadingMore}
          onLoadMore={handleLoadMore}
          hasMore={hasMore}
        /></div>
      </Container>

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        title="Delete Financial Account"
        message="Are you sure you want to delete this financial account? This action cannot be undone."
        itemName="financial account"
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};

AdminCustomerFinancialAccountsPage.propTypes = {
  onFilterClick: PropTypes.func,
  onExportClick: PropTypes.func,
};

AdminCustomerFinancialAccountsPage.defaultProps = {
  onFilterClick: () => {},
  onExportClick: () => {},
};

export default AdminCustomerFinancialAccountsPage;
